import { create } from 'zustand'

export type AdminViewMode = 'admin' | 'client' | 'vendor'

interface AppState {
  // Sidebar state
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  // Mobile menu
  mobileMenuOpen: boolean

  // Modals
  activeModal: string | null
  modalData: Record<string, unknown> | null

  // Toast notifications
  toasts: Toast[]

  // Admin dashboard view mode (preview client/vendor portals)
  adminViewMode: AdminViewMode

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  openModal: (modal: string, data?: Record<string, unknown>) => void
  closeModal: () => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  setAdminViewMode: (mode: AdminViewMode) => void
}

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

export const useAppStore = create<AppState>((set) => ({
  // Initial state
  sidebarOpen: true,
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  activeModal: null,
  modalData: null,
  toasts: [],
  adminViewMode: 'admin',

  // Actions
  toggleSidebar: () =>
    set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarCollapsed: (collapsed) =>
    set({ sidebarCollapsed: collapsed }),

  toggleMobileMenu: () =>
    set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),

  closeMobileMenu: () =>
    set({ mobileMenuOpen: false }),

  openModal: (modal, data) =>
    set({ activeModal: modal, modalData: data ?? null }),

  closeModal: () =>
    set({ activeModal: null, modalData: null }),

  addToast: (toast) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        { ...toast, id: typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}` },
      ],
    })),

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  setAdminViewMode: (mode) =>
    set({ adminViewMode: mode }),
}))

// Helper hook for toast notifications
export function useToast() {
  const addToast = useAppStore((state) => state.addToast)
  const removeToast = useAppStore((state) => state.removeToast)

  return {
    success: (title: string, message?: string) =>
      addToast({ type: 'success', title, message, duration: 3000 }),
    error: (title: string, message?: string) =>
      addToast({ type: 'error', title, message, duration: 5000 }),
    warning: (title: string, message?: string) =>
      addToast({ type: 'warning', title, message, duration: 4000 }),
    info: (title: string, message?: string) =>
      addToast({ type: 'info', title, message, duration: 3000 }),
    dismiss: removeToast,
  }
}
